import { FirebaseDocument } from "common/models/FirebaseDocument";
import BatchService from "common/services/BatchService";
import { default as firebase, firestore, storage } from "common/Firebase";
import * as _ from "lodash";
import i18n from "i18n";
import { RateLibraryStore } from "modules/rateLibrary/RateLibraryStore";
import { RateLibrary } from "modules/rateLibrary/models/RateLibrary";
import { ProfileService } from "modules/profile/ProfileService";
import { LoginStore } from "modules/login/LoginStore";
import { LoginService } from "modules/login/LoginService";
import { now } from "utils/FirebaseUtils";
import { gtag } from "ga-gtag";
import axios from "axios";

const removeProperty =
  (prop) =>
  ({ [prop]: _, ...rest }) =>
    rest;
const removeSharedUsers = removeProperty("sharedUsers");

class Service {
  async getImage(library): Promise<string> {
    try {
      var img = storage.ref(`images/${library.imageName}`);
      let file = await img.getDownloadURL();
      return file;
    } catch (error) {
      console.log("error: ", error);
      return "";
    }
  }

  async addImage(file, id): Promise<void> {
    await storage
      .ref()
      .child(`images/${id + file.name}`)
      .put(file);
  }

  async removeImage(file): Promise<void> {
    if (file) {
      try {
        await storage.ref().child(`images/${file}`).delete();
      } catch (error) {}
    }
  }

  async setRateLibrarySelected(id): Promise<void> {
    try {
      let libraryRef = firestore.collection("rateLibraries");
      let activeRef = await libraryRef
        .where(firebase.firestore.FieldPath.documentId(), "==", id)
        .get();
      let change = activeRef.docChanges()[0];
      if (change && change.doc) {
        if (change.doc.id === null) throw new Error("doc.id is null");

        let data: any = change.doc.data();

        let rateLibrary = new RateLibrary(
          data.name,
          data.publicLibrary,
          data.status,
          data.users,
          data.createdAt,
          data.description,
          data.files,
          data.location,
          data.share,
          data.image,
          data.imageName,
          data.modifiedAt,
          data.ownerName,
          data.ownerPhoto
        );

        if (change.type == "added") {
          rateLibrary.createdAt = now();
          rateLibrary.modifiedAt = rateLibrary.createdAt;
        } else if (change.type !== "removed") rateLibrary.modifiedAt = now();
        RateLibraryStore.rateLibrarySelected =
          new FirebaseDocument<RateLibrary>(change.doc.id, rateLibrary);
      } else {
        RateLibraryStore.rateLibrarySelected =
          new FirebaseDocument<RateLibrary>("", RateLibrary.getDefault());
      }
    } catch (e) {
      throw e;
    }
  }

  async fetchAvailableRateLibraries() {
    if (!RateLibraryStore.snapshotRateLibrariesFired) {
      RateLibraryStore.snapshotRateLibrariesFired = true;
      let uid = ProfileService.getUserId();

      LoginStore.clearRateLibrariesUsersSnapshot();
      LoginStore.rateLibrariesUsersSnapshot = firestore
        .collection("rateLibraries")
        .where(`users.${uid}`, "<", 2)
        .onSnapshot(
          async (snapshot: any) => {
            RateLibraryStore.myLibraries = await this.mapAndSortLibraries(
              snapshot
            );
          },
          function (error) {}
        );

      LoginStore.clearRateLibrariesPublicSnapshot();
      LoginStore.rateLibrariesPublicSnapshot = firestore
        .collection("rateLibraries")
        .where("publicLibrary", "==", true)
        .where("status", "==", "processed")
        .onSnapshot(
          async (snapshot) => {
            RateLibraryStore.publicLibraries = await this.mapAndSortLibraries(
              snapshot,
              true
            );
          },
          function (error) {}
        );
    } else {
      RateLibraryStore.loading = false;
    }
  }

  private async mapAndSortLibraries(
    snapshot: any,
    publicLibs: boolean = false
  ) {
    let uid = ProfileService.getUserId();

    let rateLibArray = new Array<FirebaseDocument<RateLibrary>>();

    await Promise.all(
      snapshot.docs.map(async (doc: any) => {
        let data: any = doc.data();

        let rateLibrary = new RateLibrary(
          data.name,
          data.publicLibrary,
          data.status,
          data.users,
          data.typeError,
          data.createdAt,
          data.description,
          data.files,
          data.location,
          data.share,
          data.image,
          data.imageName,
          data.modifiedAt,
          data.ownerName,
          data.ownerPhoto
        );
        let item = new FirebaseDocument<RateLibrary>(doc.id, rateLibrary);

        if (!publicLibs) {
          let sharedUsers = (
            await LoginService.fetchUsersByID(_.keys(item.data.users))
          ).filter(function (user) {
            return user.email !== "" && user.uid !== uid;
          });

          if (sharedUsers.length > 0) {
            item.data.sharedUsers = sharedUsers;
          }
        }

        rateLibArray.push(item);
      })
    );

    let sortedLibs = this.sortRateLibrariesByModifiedAt(rateLibArray);
    if (!publicLibs) {
      RateLibraryStore.loading = false;
    }
    return sortedLibs;
  }

  /**
   * Recebe um array com os rateLibraries e ordena de acordo com o campo modifiedAt
   * caso ele esteja undefined, ordena para o fim da lista.
   */
  public sortRateLibrariesByModifiedAt(rateLibArray) {
    return rateLibArray.sort(function (a, b) {
      if (a.data.modifiedAt == undefined) {
        return 1;
      }
      if (b.data.modifiedAt == undefined) {
        return -1;
      }
      return (
        new Date(
          b.data.modifiedAt.seconds * 1000 +
            b.data.modifiedAt.nanoseconds / 1000000
        ).getTime() -
        new Date(
          a.data.modifiedAt.seconds * 1000 +
            a.data.modifiedAt.nanoseconds / 1000000
        ).getTime()
      );
    });
  }

  public findOwnerInUsers(data: RateLibrary): string {
    let owner = "";
    if (data.users != undefined) {
      _.forEach(data.users, function (value, key) {
        if (value == 0) owner = key;
      });
    }
    return owner;
  }

  async DeleteRateLibrary(lib): Promise<void> {
    const libAnterior = lib;
    try {
      RateLibraryStore.rateLibrarySelected = lib;
      let docRef = firestore.collection("rateLibraries").doc(lib.id);

      BatchService.apply([{ type: "delete", args: [docRef] }]);

      if (libAnterior !== lib)
        RateLibraryStore.rateLibrarySelected = libAnterior;
      else
        RateLibraryStore.rateLibrarySelected =
          new FirebaseDocument<RateLibrary>("", RateLibrary.getDefault());
    } catch (e) {
      console.log("Error to delete Rate Library: ", e);
    }
  }

  async ChangeRateLibraryStatus(lib): Promise<void> {
    const libAnterior = lib;
    try {
      RateLibraryStore.rateLibrarySelected = lib;
      let docRef = firestore.collection("rateLibraries").doc(lib.id);

      let update: any = {};
      update.status = "processed";
      update.files = lib.data.files.filter((f) => f.status == "processed");
      BatchService.apply([{ type: "update", args: [docRef, { ...update }] }]);

      if (libAnterior !== lib)
        RateLibraryStore.rateLibrarySelected = libAnterior;
      else
        RateLibraryStore.rateLibrarySelected =
          new FirebaseDocument<RateLibrary>("", RateLibrary.getDefault());
    } catch (e) {
      console.log("Error to delete Rate Library: ", e);
    }
  }

  async changeRateLibraryImage(lib, file) {
    await this.removeImage(lib.data.imageName);
    await this.addImage(file, lib.id);
    let url = await this.getImage({ imageName: lib.id + file.name });
    await firestore
      .collection("rateLibraries")
      .doc(lib.id)
      .update({ imageName: lib.id + file.name, image: url });
  }

  async updateFollowerDatabase(lib) {
    let uid = ProfileService.getUserId();
    if (lib.data.users[uid]) {
      gtag("event", "deixar_seguir_banco_composicao_publico", {
        deixar_seguir_banco_composicao_publico: 0,
      });
      delete lib.data.users[uid];
    } else {
      gtag("event", "seguir_banco_composicao_publico", {
        seguir_banco_composicao_publico: 0,
      });
      lib.data.users[uid] = 2;
    }
    await firestore
      .collection("rateLibraries")
      .doc(lib.id)
      .update({ users: lib.data.users });
  }

  async updateSharedDatabase(users) {
    let uid = ProfileService.getUserId();
    var lib = RateLibraryStore.openShareLibrary;
    lib.data.users = new Object();
    lib.data.users[uid] = 0;

    for (let index = 0; index < users.length; index++) {
      const element = users[index];
      lib.data.users[element] = 1;
    }

    await firestore
      .collection("rateLibraries")
      .doc(lib.id)
      .update({ users: lib.data.users });
  }

  async changeRateLibraryName(lib, name) {
    await firestore.collection("rateLibraries").doc(lib.id).update({ name });
  }

  async restartProcesses(lib, files) {
    await firestore.collection("rateLibraries").doc(lib.id).update({
      status: "pending",
      files,
    });

    let ratelibrary = await firestore
      .collection("rateLibraries")
      .doc(lib.id)
      .get();

    let data = ratelibrary.data();
    let file: any;
    let filesToProcess: any;
    let indexFile: number = 0;
    if (data) {
      filesToProcess = data.files.filter((f) => f.status == "processing");
      file = filesToProcess;
      indexFile = data.files.indexOf(file[0]);
    }
    let verifyFile = await axios.post(
      `${process.env.REACT_APP_CHECK_TYPE_FILE}`,
      {
        fileName: lib.id + file.name,
        id: lib.id,
        fileExtension: this.getFileExtension(file.name),
      }
    );

    if (verifyFile.data == 1) {
      // await axios.post(`${process.env.REACT_APP_IMPORT_RATELIBRARY}`, {
      //   id: lib.id,
      //   indexFile: indexFile,
      // });
      const sortDataTask = firebase.functions().httpsCallable("sortDataTask");

      sortDataTask(data);

      return;
    }
    await axios.post(`${process.env.REACT_APP_IMPORT_RESOURCE}`, {
      id: lib.id,
      fileName: lib.id + file.name,
      fileExtension: this.getFileExtension(file.name),
      indexFile: indexFile,
    });
  }

  async changeRateLibraryLocation(lib, location) {
    await firestore
      .collection("rateLibraries")
      .doc(lib.id)
      .update({ location });
  }

  getFileExtension(filename) {
    return filename.split(".").pop();
  }

  deleteKeyFromDictionary(dict, key) {
    const dictCopy = { ...dict };
    delete dictCopy[key];
    return dictCopy;
  }

  /**
   * @description Adiciona o banco de composições ou planilha complementar
   * @param file
   * @param name
   * @param description
   * @param location
   * @param share
   */
  async AddLibrary(file, name, description, location, share): Promise<void> {
    try {
      if (LoginStore.userAuth) {
        let fileObj = [
          { fileName: file.name, message: "", status: "uploading" },
        ];
        let uid = ProfileService.getUserId();

        let lib = new RateLibrary(
          name,
          LoginStore.userAuth.rubk !== undefined
            ? LoginStore.userAuth.rubk
            : false,
          "uploading",
          { [uid]: 0 },
          "",
          now(),
          description,
          fileObj,
          location,
          share,
          "",
          "",
          now(),
          LoginStore.userAuth.displayName,
          LoginStore.userAuth.photoURL
        );

        let addedLib = await firestore
          .collection("rateLibraries")
          .add(removeSharedUsers(lib));

        try {
          RateLibraryStore.progressStatusUpload[addedLib.id] = 0;
          RateLibraryStore.progressStatusUploadFile[addedLib.id + "file"] =
            file.name;

          let uploadTask = storage
            .ref()
            .child("rateLibraries/" + addedLib.id + file.name)
            .put(file);
          uploadTask.on(
            firebase.storage.TaskEvent.STATE_CHANGED,
            function (snapshot) {
              let perc = Math.trunc(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              RateLibraryStore.progressStatusUpload[addedLib.id] =
                perc < 100 ? perc : 99;
            }.bind(this)
          );
          await uploadTask;

          RateLibraryStore.progressStatusUpload = this.deleteKeyFromDictionary(
            RateLibraryStore.progressStatusUpload,
            addedLib.id
          );
          RateLibraryStore.progressStatusUploadFile =
            this.deleteKeyFromDictionary(
              RateLibraryStore.progressStatusUploadFile,
              addedLib.id + "file"
            );

          lib.status = "pending";
          fileObj[0].fileName = addedLib.id + fileObj[0].fileName;
          fileObj[0].status = "pending";
          lib.files = fileObj;

          let data = {
            id: addedLib.id,
            fileName: addedLib.id + file.name,
            fileExtension: this.getFileExtension(file.name),
            indexFile: 0,
          };

          let verifyFile = await axios.post(
            `${process.env.REACT_APP_CHECK_TYPE_FILE}`,
            {
              fileName: addedLib.id + file.name,
              id: addedLib.id,
              fileExtension: this.getFileExtension(file.name),
            }
          );

          if (verifyFile.data == 1) {
            try {
              const sortDataTask = firebase
                .functions()
                .httpsCallable("sortDataTask");

              sortDataTask(data);
            } catch (e) {}
          }

          if (verifyFile.data == 2) {
            try {
              const resourceSheetTask = firebase
                .functions()
                .httpsCallable("resourceSheetTask");

              resourceSheetTask({
                id: addedLib.id,
              });
            } catch (e) {}
          }

          await firestore
            .collection("rateLibraries")
            .doc(addedLib.id)
            .set(removeSharedUsers(lib));

          gtag("event", "importacao_banco_composicao", {
            importacao_banco_composicao: 0,
          });
        } catch (e) {
          storage
            .ref()
            .child("rateLibraries/" + addedLib.id + file.name)
            .delete();

          firestore.collection("rateLibraries").doc(addedLib.id).delete();

          RateLibraryStore.snackMessage =
            i18n.rateLibraries.cardProcessing.errorMessage;
          RateLibraryStore.snackOpen = true;
          RateLibraryStore.progressStatusUpload = this.deleteKeyFromDictionary(
            RateLibraryStore.progressStatusUpload,
            addedLib.id
          );
          RateLibraryStore.progressStatusUploadFile =
            this.deleteKeyFromDictionary(
              RateLibraryStore.progressStatusUploadFile,
              addedLib.id + "file"
            );
        }
      }
    } catch (e) {
      RateLibraryStore.snackMessage =
        i18n.rateLibraries.cardProcessing.errorMessage;
      RateLibraryStore.snackOpen = true;
    }
  }

  async AddFileToLibrary(file, lib, libId): Promise<void> {
    let originalLibCopy = lib as any;
    let indexFile: number = 0;
    try {
      let originalFileCollection = [...lib.files];

      lib.status = "processing";
      let fileObj = { fileName: file.name, message: "", status: "processing" };
      lib.files.push(fileObj);

      // indexFile = lib.files.length - 1;

      await firestore
        .collection("rateLibraries")
        .doc(libId)
        .set(removeSharedUsers(lib));

      RateLibraryStore.progressStatusUpload[libId] = 0;
      RateLibraryStore.progressStatusUploadFile[libId + "file"] = file.name;

      let uploadTask = storage
        .ref()
        .child("rateLibraries/" + libId + file.name)
        .put(file);
      uploadTask.on(
        firebase.storage.TaskEvent.STATE_CHANGED,
        function (snapshot) {
          RateLibraryStore.progressStatusUpload[libId] = Math.trunc(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
        }.bind(this)
      );
      await uploadTask;

      RateLibraryStore.progressStatusUpload = this.deleteKeyFromDictionary(
        RateLibraryStore.progressStatusUpload,
        libId
      );
      RateLibraryStore.progressStatusUploadFile = this.deleteKeyFromDictionary(
        RateLibraryStore.progressStatusUploadFile,
        libId + "file"
      );

      // lib.status = "pending";
      fileObj.fileName = libId + fileObj.fileName;
      fileObj.status = "pending";
      originalFileCollection.push(fileObj);
      lib.files = originalFileCollection;

      await firestore
        .collection("rateLibraries")
        .doc(libId)
        .set(removeSharedUsers(lib));
    } catch (e) {
      try {
        storage
          .ref()
          .child("rateLibraries/" + libId + file.name)
          .delete();

        await firestore
          .collection("rateLibraries")
          .doc(libId)
          .set(removeSharedUsers(originalLibCopy));
      } catch (innerExc) {
        console.log("erro: ", innerExc);
      }

      RateLibraryStore.snackMessage =
        i18n.rateLibraries.cardProcessing.errorMessage;
      RateLibraryStore.snackOpen = true;
      RateLibraryStore.progressStatusUpload = this.deleteKeyFromDictionary(
        RateLibraryStore.progressStatusUpload,
        libId
      );
      RateLibraryStore.progressStatusUploadFile = this.deleteKeyFromDictionary(
        RateLibraryStore.progressStatusUploadFile,
        libId + "file"
      );
    }

    let verifyFile = await axios.post(
      `${process.env.REACT_APP_CHECK_TYPE_FILE}`,
      {
        fileName: libId + file.name,
        id: libId,
        fileExtension: this.getFileExtension(file.name),
      }
    );

    if (verifyFile.data == 1) {
      try {
        const sortDataTask = firebase.functions().httpsCallable("sortDataTask");

        sortDataTask({
          id: libId,
          fileName: libId + file.name,
          fileExtension: this.getFileExtension(file.name),
          indexFile: indexFile,
        });
      } catch (e) {}

      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_IMPORT_RESOURCE}`, {
        id: libId,
        fileName: libId + file.name,
        fileExtension: this.getFileExtension(file.name),
        indexFile: indexFile,
      });
    } catch (error) {}
  }
}

let RateLibraryService = new Service();
export { RateLibraryService };



// WEBPACK FOOTER //
// ./src/modules/rateLibrary/RateLibraryService.ts